/**
 * What this frontend has to agree with the CMS about.
 *
 * The API serves `url` on every entry and every menu item, so rendering needs
 * none of this. Routing does: a request arrives as path segments and something
 * has to invert that URL back into the type, slug and locale the API asks for.
 * These are the two facts that inversion needs, and both are configuration on
 * the CMS side (`cms.locales`, `cms.content_type_prefixes`) rather than
 * constants — which is why they are environment variables here.
 */
const configured = (process.env.CMS_LOCALES ?? 'en')
    .split(',')
    .map((locale) => locale.trim().toLowerCase())
    .filter((locale) => locale !== '')

export const LOCALES: string[] = configured.length > 0 ? configured : ['en']

/** The first configured locale, which the CMS serves unprefixed. */
export const DEFAULT_LOCALE: string = LOCALES[0]

/** Mirrors `cms.content_type_prefixes`. An empty prefix means root-level. */
export const TYPE_PREFIXES: Record<string, string> = {
    page: '',
    post: 'blog',
}

/**
 * Where the CMS lives as a *browser* sees it. Used for form posts and for the
 * live-preview origin check, so it has to match the panel's own origin
 * exactly — scheme and port included.
 */
export const CMS_ORIGIN: string = process.env.NEXT_PUBLIC_CMS_ORIGIN ?? 'https://filamentcms.ddev.site'

/**
 * Where server-side fetches go. May differ from CMS_ORIGIN by scheme, which is
 * the whole reason it is a separate variable: Node does not trust DDEV's
 * mkcert root unless it is told to, and a demo should not need that to run.
 */
export const CMS_API_URL: string = process.env.CMS_API_URL ?? 'http://filamentcms.ddev.site/api/v1'

/**
 * This site's own origin, when it has been told.
 *
 * One thing needs it, and it is about the deployment rather than the content:
 * the canonical base every page's metadata is resolved against.
 */
export const SITE_URL: string | undefined = process.env.NEXT_PUBLIC_SITE_URL
